import React, { useEffect, useState } from "react";
import Stepper from "../../common/Steps/Stepper";

const ProgressSteps = ({
  stepsData = [],
  reportDate,
  loggedInEmail,
  handleStepApproval,
  isGlobal = null,
  isModelerReport = null,
  isTreasuryKRIReport = null,
  isKRISummaryReport = null,
  isSFCKRIGlobalSummaryReport = null,
  isRegionKRIGlobalSummaryReport = null,
  isTFSCKRIGlobalSummaryReport = null,
  hideProgressSeries = false,
  skipStepFilter = false,
} = {}) => {
  const [steps, setSteps] = useState([]);

  const getLatestApprovalData = (step) => {
    const approvers = step.approvalStepApprovers || [];
    const allApprovalData = approvers.flatMap(
      (approver) => approver.approvalDataList || [],
    );

    if (allApprovalData.length === 0) return null;

    return allApprovalData.reduce((latest, current) => {
      if (!latest) return current;
      if (!current.approvalStatusChangeTime) return latest;
      if (!latest.approvalStatusChangeTime) return current;

      return new Date(current.approvalStatusChangeTime) >
        new Date(latest.approvalStatusChangeTime)
        ? current
        : latest;
    }, null);
  };

  useEffect(() => {
    if (!stepsData || stepsData.length === 0) {
      setSteps([]);
      return;
    }

    // 1) Build full flow with latest approval data
    let allSteps = stepsData.map((step) => {
      const latestApprovalData = getLatestApprovalData(step);
      return {
        ...step,
        latestApprovalData,
        status: latestApprovalData ? latestApprovalData.approvalStatus : null,
        comment: latestApprovalData?.approvalComments || "",
      };
    });

    // 2) Find latest action timestamp in the full flow
    let latestActionIndex = -1;
    let latestActionTime = null;

    allSteps.forEach((step, idx) => {
      const ts = step.latestApprovalData?.approvalStatusChangeTime
        ? new Date(step.latestApprovalData.approvalStatusChangeTime)
        : null;

      if (ts && (!latestActionTime || ts > latestActionTime)) {
        latestActionTime = ts;
        latestActionIndex = idx;
      }
    });

    // 3) Apply full-flow approval logic
    if (latestActionIndex === -1) {
      // nothing acted on yet
      allSteps = allSteps.map((step, idx) => ({
        ...step,
        status: idx === 0 ? "PENDING" : "UPCOMING",
        comment: "",
      }));
    } else {
      const latestStep = allSteps[latestActionIndex];
      const latestStatus = latestStep.latestApprovalData?.approvalStatus;

      if (latestStatus === "APPROVED") {
        // all steps before latest approved step are approved
        allSteps = allSteps.map((step, idx) => {
          if (idx <= latestActionIndex) {
            return { ...step, status: "APPROVED" };
          }
          if (idx === latestActionIndex + 1) {
            return { ...step, status: "PENDING" };
          }
          return { ...step, status: "UPCOMING", comment: "" };
        });
      } else if (latestStatus === "REJECTED") {
        // rejected step stays rejected, previous step becomes pending
        allSteps = allSteps.map((step, idx) => {
          // If the first step is rejected, keep it enabled for re-review
          if (latestActionIndex === 0) {
            if (idx === 0) {
              return { ...step, status: "PENDING" };
            }
            return { ...step, status: "UPCOMING", comment: "" };
          }

          // Normal reject flow for other steps
          if (idx < latestActionIndex - 1) {
            return { ...step, status: "APPROVED" };
          }
          if (idx === latestActionIndex - 1) {
            return { ...step, status: "PENDING" };
          }
          if (idx === latestActionIndex) {
            return { ...step, status: "REJECTED" };
          }
          return { ...step, status: "UPCOMING", comment: "" };
        });
      } else {
        allSteps = allSteps.map((step, idx) => ({
          ...step,
          status: idx === 0 ? "PENDING" : "UPCOMING",
        }));
      }
    }

    if (skipStepFilter) {
      setSteps(allSteps);
      return;
    }

    const visibleSteps = allSteps.filter((step) => {
      // Modeler Report
      if (
        isGlobal === false &&
        isModelerReport === true &&
        isTreasuryKRIReport === false &&
        isKRISummaryReport === false &&
        isSFCKRIGlobalSummaryReport === false &&
        isRegionKRIGlobalSummaryReport === false &&
        isTFSCKRIGlobalSummaryReport === false
      ) {
        return (
          step.global === false &&
          step.modelerReport === true &&
          step.treasuryKRIReport === false &&
          step.kriSummaryReport === false &&
          step.sfcKRIGlobalSummaryReport === false &&
          step.regionKRIGlobalSummaryReport === false &&
          step.tfscKRIGlobalSummaryReport === false
        );
      }
      // Global Modeler Report
      if (
        isGlobal === true &&
        isModelerReport === true &&
        isTreasuryKRIReport === false &&
        isKRISummaryReport === false &&
        isSFCKRIGlobalSummaryReport === false &&
        isRegionKRIGlobalSummaryReport === false &&
        isTFSCKRIGlobalSummaryReport === false
      ) {
        return (
          step.global === true &&
          step.modelerReport === true &&
          step.treasuryKRIReport === false &&
          step.kriSummaryReport === false &&
          step.sfcKRIGlobalSummaryReport === false &&
          step.regionKRIGlobalSummaryReport === false &&
          step.tfscKRIGlobalSummaryReport === false
        );
      }
      // Treasury KRI Report
      if (
        isGlobal === false &&
        isModelerReport === false &&
        isTreasuryKRIReport === true &&
        isKRISummaryReport === false &&
        isSFCKRIGlobalSummaryReport === false &&
        isRegionKRIGlobalSummaryReport === false &&
        isTFSCKRIGlobalSummaryReport === false
      ) {
        return (
          step.global === false &&
          step.modelerReport === false &&
          step.treasuryKRIReport === true &&
          step.kriSummaryReport === false &&
          step.sfcKRIGlobalSummaryReport === false &&
          step.regionKRIGlobalSummaryReport === false &&
          step.tfscKRIGlobalSummaryReport === false
        );
      }
      // Treasury KRI Global Report
      if (
        isGlobal === true &&
        isModelerReport === false &&
        isTreasuryKRIReport === true &&
        isKRISummaryReport === false &&
        isSFCKRIGlobalSummaryReport === false &&
        isRegionKRIGlobalSummaryReport === false &&
        isTFSCKRIGlobalSummaryReport === false
      ) {
        return (
          step.global === true &&
          step.modelerReport === false &&
          step.treasuryKRIReport === true &&
          step.kriSummaryReport === false &&
          step.sfcKRIGlobalSummaryReport === false &&
          step.regionKRIGlobalSummaryReport === false &&
          step.tfscKRIGlobalSummaryReport === false
        );
      }
      // KRI Summary Report
      if (
        isGlobal === false &&
        isModelerReport === false &&
        isTreasuryKRIReport === false &&
        isKRISummaryReport === true &&
        isSFCKRIGlobalSummaryReport === false &&
        isRegionKRIGlobalSummaryReport === false &&
        isTFSCKRIGlobalSummaryReport === false
      ) {
        return (
          step.global === false &&
          step.modelerReport === false &&
          step.treasuryKRIReport === false &&
          step.kriSummaryReport === true &&
          step.sfcKRIGlobalSummaryReport === false &&
          step.regionKRIGlobalSummaryReport === false &&
          step.tfscKRIGlobalSummaryReport === false
        );
      }
      // KRI global Summary Report
      if (
        isGlobal === true &&
        isModelerReport === false &&
        isTreasuryKRIReport === false &&
        isKRISummaryReport === true &&
        isSFCKRIGlobalSummaryReport === false &&
        isRegionKRIGlobalSummaryReport === false &&
        isTFSCKRIGlobalSummaryReport === false
      ) {
        return (
          step.global === true &&
          step.modelerReport === false &&
          step.treasuryKRIReport === false &&
          step.kriSummaryReport === true &&
          step.sfcKRIGlobalSummaryReport === false &&
          step.regionKRIGlobalSummaryReport === false &&
          step.tfscKRIGlobalSummaryReport === false
        );
      }

      // All Global
      if (
        isGlobal === true &&
        isModelerReport === false &&
        isTreasuryKRIReport === false &&
        isKRISummaryReport === false &&
        isSFCKRIGlobalSummaryReport === false &&
        isRegionKRIGlobalSummaryReport === false &&
        isTFSCKRIGlobalSummaryReport === false
      ) {
        return (
          step.global === true &&
          step.modelerReport === false &&
          step.treasuryKRIReport === false &&
          step.kriSummaryReport === false &&
          step.sfcKRIGlobalSummaryReport === false &&
          step.regionKRIGlobalSummaryReport === false &&
          step.tfscKRIGlobalSummaryReport === false
        );
      }

      // All non Global
      if (
        isGlobal === false &&
        isModelerReport === false &&
        isTreasuryKRIReport === false &&
        isKRISummaryReport === false &&
        isSFCKRIGlobalSummaryReport === false &&
        isRegionKRIGlobalSummaryReport === false &&
        isTFSCKRIGlobalSummaryReport === false
      ) {
        return (
          step.global === false &&
          step.modelerReport === false &&
          step.treasuryKRIReport === false &&
          step.kriSummaryReport === false &&
          step.sfcKRIGlobalSummaryReport === false &&
          step.regionKRIGlobalSummaryReport === false &&
          step.tfscKRIGlobalSummaryReport === false
        );
      }

      return false;
    });

    setSteps(visibleSteps);
  }, [
    stepsData,
    isGlobal,
    isModelerReport,
    isTreasuryKRIReport,
    isKRISummaryReport,
    isSFCKRIGlobalSummaryReport,
    isRegionKRIGlobalSummaryReport,
    isTFSCKRIGlobalSummaryReport,
    skipStepFilter,
  ]);

  const handleAction = (index, type, comment) => {
    const currentStep = steps[index];
    const approvers = currentStep.approvalStepApprovers || [];
    const currentApprover = approvers.find(
      (a) => a.email.toLowerCase() === loggedInEmail?.toLowerCase(),
    );

    if (!currentApprover) {
      alert("You are not authorized to do this action");
      return;
    }

    handleStepApproval({
      currentStep,
      currentApprover,
      type,
      comment,
      reportDate,
    }).catch((error) => {
      console.error(
        "Error saving approval data or fetching updated flow:",
        error,
      );
    });
  };

  return (
    <Stepper
      stepsData={steps}
      steps={steps}
      setSteps={setSteps}
      onAction={handleAction}
      hideProgressSeries={hideProgressSeries}
    />
  );
};

export default ProgressSteps;
